import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { AdminService } from 'proto/gen/ts/shirt/v1/admin_pb';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminOrderRow, TEST_IDS } from '../../utils/testIds';
import { transport } from '../../utils/transport';
const adminClient = createClient(AdminService, transport);
const TABS = [
    {
        label: 'To Pack',
        value: 'pending'
    },
    {
        label: 'Packed',
        value: 'packed'
    },
    {
        label: 'Shipped',
        value: 'shipped'
    },
    {
        label: 'Delivered',
        value: 'delivered'
    },
    {
        label: 'Cancelled',
        value: 'cancelled'
    },
    {
        label: 'All',
        value: ''
    }
];
function statusBadge(s) {
    switch(s){
        case 'pending':
            return 'bg-yellow-100 text-yellow-800';
        case 'packed':
            return 'bg-blue-100 text-blue-800';
        case 'shipped':
            return 'bg-indigo-100 text-indigo-800';
        case 'delivered':
            return 'bg-green-100 text-green-800';
        case 'cancelled':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-stone-100 text-stone-700';
    }
}
function formatCents(cents) {
    const n = typeof cents === 'bigint' ? Number(cents) : cents;
    return `$${(n / 100).toFixed(2)}`;
}
function shortID(id) {
    return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}
function formatDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    } catch  {
        return iso;
    }
}
export default function OrderDashboard() {
    const [filter, setFilter] = useState('pending');
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'adminOrders',
            filter
        ],
        queryFn: ()=>adminClient.listOrders({
                fulfillmentStatus: filter
            }),
        refetchInterval: 30000
    });
    const orders = data?.orders ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
        children: [
            /*#__PURE__*/ _jsx("h1", {
                className: "font-heading text-2xl font-bold text-stone-900 mb-6",
                children: "Orders"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-1 mb-4 border-b border-stone-200",
                children: TABS.map((tab)=>/*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setFilter(tab.value),
                        className: `px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px transition-colors ${filter === tab.value ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-900'}`,
                        children: tab.label
                    }, tab.value || 'all'))
            }),
            isError ? /*#__PURE__*/ _jsx("p", {
                className: "text-red-600",
                children: "Failed to load orders."
            }) : isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-stone-500",
                children: "Loading orders..."
            }) : orders.length === 0 ? /*#__PURE__*/ _jsx("div", {
                "data-testid": TEST_IDS.adminOrdersTable,
                className: "bg-white border-2 border-stone-100 p-8 text-center text-stone-500 text-sm",
                children: "No orders in this queue."
            }) : /*#__PURE__*/ _jsx("div", {
                "data-testid": TEST_IDS.adminOrdersTable,
                className: "bg-white border-2 border-stone-100 divide-y divide-stone-100",
                children: orders.map((o)=>/*#__PURE__*/ _jsxs(Link, {
                        "data-testid": adminOrderRow(o.id),
                        to: `/admin/orders/${o.id}`,
                        className: "flex items-center gap-4 p-4 hover:bg-stone-50",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 flex-wrap",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-sm font-mono text-stone-700",
                                                children: shortID(o.id)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${statusBadge(o.fulfillmentStatus)}`,
                                                children: o.fulfillmentStatus
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-stone-500 mt-1 truncate",
                                        children: o.email
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-right",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-semibold text-stone-900",
                                        children: formatCents(o.totalCents)
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-stone-400 mt-1",
                                        children: formatDate(o.paidAt || o.createdAt)
                                    })
                                ]
                            })
                        ]
                    }, o.id))
            })
        ]
    });
}
